import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { Option } from '@interfaces/common/option';
import { TranslateService } from '@ngx-translate/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { FinancialInstitutionService } from '@services/http/financial-institution.service';
import {
  DerivativeOperationQuoteFromAi,
  NdfCalculatorService,
} from '@services/http/ndf-calculator.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { FileSelectEvent } from 'primeng/fileupload';

@Component({
  selector: 'app-create-operations-with-cog-modal',
  templateUrl: './create-operations-with-cog-modal.component.html',
  styleUrls: ['./create-operations-with-cog-modal.component.scss'],
  standalone: false,
})
export class CreateOperatinsWithCogModalComponent extends VaultBaseComponent implements OnInit {
  /** Texto colado para o Cog extrair as operações (antes um editor Toast UI; Bug 3680). */
  promptText = '';
  selectedImage: string | null = null;
  selectedFile: File | null = null;
  isBrowser: boolean;
  innerFinancialInstitutions: Option[] = [];

  @Output() sendOperations = new EventEmitter<DerivativeOperationQuoteFromAi[] | null>();

  constructor(
    translateService: TranslateService,
    private ndfCalculatorService: NdfCalculatorService,
    private financialInstitutionService: FinancialInstitutionService,
    private toastHelper: ToastHelperService,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    super(translateService);
    this.isBrowser = isPlatformBrowser(this.platformId);
  }
  ngOnInit(): void {
    this.getFinancialInstitutions();
  }

  getFinancialInstitutions() {
    this.financialInstitutionService.getFinancialInstitutions().subscribe({
      next: response => {
        if (response) {
          this.innerFinancialInstitutions = (response?.data || []).map(item => {
            return {
              label: `${item.displayName} | ${item.compensationCode}`,
              value: item.id,
              data: item,
            };
          });
        }
      },
      error: error => {
        console.error(error);
      },
    });
  }
  selectedFinancialInstitution: string | null = null;

  @ViewChild('dialog') dialog!: DialogComponent;

  open() {
    this.promptText = '';
    this.dialog.open('', '', '', 'Cancelar', '', 'create-with-cog', false, false);
  }

  /**
   * Texto digitado ou colado: vale o texto, e a imagem escolhida antes sai (o Cog lê um ou outro).
   * @param text Texto atual do campo.
   */
  onPromptTextChange(text: string) {
    this.promptText = text ?? '';
    if (this.promptText && this.selectedImage) this.removeImage();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleImageFile(files[0]);
    }
  }

  onPaste(event: ClipboardEvent) {
    const items = event.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const file = items[i].getAsFile();
          if (file) {
            this.handleImageFile(file);
          }
        }
      }
    }
  }

  onFileSelect(event: FileSelectEvent) {
    const file = event.files[0];
    if (file) {
      this.handleImageFile(file);
      this.promptText = '';
    }
  }

  handleImageFile(file: File) {
    this.selectedFile = file;
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = e => {
        this.selectedImage = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  removeImage() {
    this.selectedImage = null;
    this.selectedFile = null;
  }

  processOperationsWithCog() {
    if (!this.selectedFile && !this.promptText.trim()) {
      this.toastHelper.showToastError(this.t(this.tk.General.SendFileOrTextToExtractOperations));
      return;
    }

    this.isLoading = true;
    const financialInstitutionId = this.selectedFinancialInstitution;
    if (this.selectedImage == null) {
      this.ndfCalculatorService
        .getOperationsFromTextWithCog(this.promptText)
        .subscribe(operations => {
          if (operations) {
            for (const op of operations) {
              op.financialInstitutionId = financialInstitutionId as string;
            }
          }
          this.sendOperations.emit(operations);
          this.dialog.close();
          this.isLoading = false;
        });
    } else {
      this.ndfCalculatorService
        .getOperationsFromFileWithCogFromFile(this.selectedFile)
        .subscribe(operations => {
          if (operations) {
            for (const op of operations) {
              op.financialInstitutionId = financialInstitutionId as string;
            }
          }
          this.sendOperations.emit(operations);
          this.dialog.close();
          this.isLoading = false;
        });
    }
  }
}
